import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'
import {
  Button,
  Popconfirm,
  Steps,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'
import { blue, redPrimary } from '../../color'
import { ASSIGNEE_TYPE, PROPOSAL_STATUS } from '../../constants'
import date_format from '../../date_format'
import { AvatarHasName } from '../Common/Avatar'
import SelectPeoplePopup from '../SelectPeoplePopup'

const { Step } = Steps

const StepsWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 30px;
  position: relative;
  .steps {
    width: 100% !important;
    display: ${({ count, i }) => (count > i ? '-webkit-box' : '')};

    transform: translateX(30px);
    .ant-steps-item-title,
    .ant-steps-item-description {
      color: #000000d9 !important;
    }
    .ant-steps-item-wait .ant-steps-item-icon > .ant-steps-icon {
      color: #000000d9 !important;
      font-weight: 500;
    }
    
    .ant-steps-item-content {
      width: ${({ i }) => (i == 4 ? '225px' : '220px')}; !important;
      transform: translateX(-30px);
    }
    .tag-selected {
      border-radius: 35px;
      padding: 2px 8px 2px 3px;
      display: flex;
      margin-right: 0;
      align-items: center;
    }
  }
  .ant-steps-label-vertical.ant-steps-small:not(.ant-steps-dot) .ant-steps-item-icon {
    margin-left: 70px !important;
  }
  .ant-steps-label-vertical .ant-steps-item-tail {
    margin-left: 86px;
    width: 90%;
  }
  @media screen and (min-width: 1600px) {
    .steps {
      display: ${({ i }) => (i == 5 ? 'flex' : '-webkit-box')} !important;
    }
  }
`

const getInfoUser = (username, departmentWithUserList) => {
  const defaultUser = {
    full_name: username || 'Unknown User',
    image_id: null,
    username: username || null,
    department: null,
  }

  if (!username || !departmentWithUserList) {
    return defaultUser
  }

  for (const department of departmentWithUserList) {
    if (!department?.users) continue
    const userTarget = department.users.find(
      user => user.user_name === username
    )
    if (userTarget) {
      return {
        full_name:
          userTarget.name_uppercase || userTarget.full_name || username,
        image_id: userTarget.image_id || null,
        username: username,
        department: department.name || null,
      }
    }
  }

  return defaultUser
}

const renderStatus = (status, isRejected) => {
  const styleTag = {
    marginTop: 8,
    borderRadius: 10,
  }

  if (status === PROPOSAL_STATUS.PENDING) {
    return (
      <Tag
        color={isRejected ? null : 'geekblue'}
        style={{ ...styleTag, opacity: isRejected ? 0.5 : 1 }}>
        Đến lượt duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.WAITING) {
    return (
      <Tag
        color={isRejected ? null : 'orange'}
        style={{ ...styleTag, opacity: isRejected ? 0.5 : 1 }}>
        Chờ duyệt
      </Tag>
    )
  }

  if (status === PROPOSAL_STATUS.CREATED) {
    return (
      <Tag color={'cyan'} style={styleTag}>
        Đã tạo
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.APPROVED) {
    return (
      <Tag color={'green'} style={styleTag}>
        Đã duyệt
      </Tag>
    )
  }
  if (status === PROPOSAL_STATUS.REJECTED) {
    return (
      <Tag color={'red'} style={styleTag}>
        Đã từ chối
      </Tag>
    )
  }
}

const renderTime = level => {
  const styleTag = {
    marginTop: 4,
  }

  const textCreated = date_format.HH_mm_DD_MM_YYYY(level.created_at)
  const textUpdated = date_format.HH_mm_DD_MM_YYYY(level.updated_at)

  if (level.status === PROPOSAL_STATUS.CREATED) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textCreated}
      </p>
    )
  }
  if (
    level.status === PROPOSAL_STATUS.APPROVED ||
    level.status === PROPOSAL_STATUS.REJECTED
  ) {
    return (
      <p color={'cyan'} style={styleTag}>
        {textUpdated}
      </p>
    )
  }

  return <></>
}

export const ASSIGNEE_TYPE_CONFIG = [
  ASSIGNEE_TYPE.CREATE,
  ASSIGNEE_TYPE.COMBINER,
  ASSIGNEE_TYPE.HANDLER,
  ASSIGNEE_TYPE.LEADER,
  ASSIGNEE_TYPE.UNIT_LEADER,
  ASSIGNEE_TYPE.HDQT,
  ASSIGNEE_TYPE.KT_KTNB,
  ASSIGNEE_TYPE.lanh_dao_vimc,
  ASSIGNEE_TYPE.ban_kt_ktnb,
]

const ProposalStepApprove = props => {
  const {
    proposalStore,
    selectPeopleStore,
    approvalLevelList,
    styleCre,
    onRemoveApprovalLevel,
  } = props

  const { originDepartmentWithUserList, selectUserData } = selectPeopleStore
  const [currentStep, setCurrentStep] = useState(0)
  const [approvalLevels, setApprovalLevels] = useState([])
  const [isVisibleSelectUserPopup, setIsVisibleSelectUserPopup] = useState(
    false
  )
  const [statusStep, setStatusStep] = useState('process')

  useEffect(() => {
    const length = approvalLevelList.length
    if (approvalLevelList.length === 0) return
    let step = 0
    const pendingStep = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.PENDING
    )
    const endStep = approvalLevelList[length - 1]
    const stepRejected = approvalLevelList.find(
      item => item.status === PROPOSAL_STATUS.REJECTED
    )
    if (pendingStep) {
      step = pendingStep.level
    }
    if (endStep.status === PROPOSAL_STATUS.APPROVED) {
      step = length
    }
    if (stepRejected) {
      step = stepRejected.level
      setStatusStep('error')
    } else {
      setStatusStep('')
    }
    setCurrentStep(step)
  }, [approvalLevelList])

  useEffect(() => {
    const approvalLevelConvert = approvalLevelList.map(item => ({
      ...item,
      user: getInfoUser(item.username, originDepartmentWithUserList),
    }))
    setApprovalLevels(approvalLevelConvert)
  }, [approvalLevelList, originDepartmentWithUserList])

  useEffect(() => {
    // Chỉ chạy khi có thay đổi từ selectUserData, không can thiệp vào data restore
    let hasNewUserSelection = false

    const approvalLevelsUpdate = approvalLevels.map(item => {
      if (!item.allow_edit) {
        return item
      }

      const levelAllowEditList = approvalLevels.filter(el => el.allow_edit)
      const assignTypeIndex = levelAllowEditList.findIndex(
        el => el.code === item.code
      )

      const assigneeType =
        assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
          ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
          : `DYNAMIC_LEVEL_${assignTypeIndex}`

      const selectedUser =
        selectUserData[assigneeType]?.length > 0
          ? selectUserData[assigneeType][0]
          : null

      if (selectedUser && selectedUser.id !== item.username) {
        // Có user mới được chọn, khác với user hiện tại
        hasNewUserSelection = true
        const userInfo = getInfoUser(
          selectedUser.id,
          originDepartmentWithUserList
        )
        return {
          ...item,
          username: selectedUser.id,
          user: {
            full_name:
              selectedUser.full_name || userInfo.full_name || 'Unknown User',
            image_id: selectedUser.image_id || userInfo.image_id,
            username: selectedUser.id,
            department: userInfo.department,
          },
        }
      } else {
        // Không có thay đổi, giữ nguyên
        return item
      }
    })

    // Update both local state and proposal store when there are changes
    if (hasNewUserSelection) {
      setApprovalLevels(approvalLevelsUpdate)
      proposalStore.setDefaultApprovalConfigUpdate(approvalLevelsUpdate)
    } else {
      // Even if no new selection, ensure proposal store is in sync
      proposalStore.setDefaultApprovalConfigUpdate(approvalLevels)
    }
  }, [
    selectUserData,
    originDepartmentWithUserList,
    proposalStore,
    approvalLevels,
  ])

  const handleSubmitSelectUser = useCallback(() => {
    setIsVisibleSelectUserPopup(false)
  }, [])

  const handleOpenSelectUser = useCallback(
    level => {
      const levelAllowEditList = approvalLevels.filter(item => item.allow_edit)
      const indexLevelAllowEdit = levelAllowEditList.findIndex(
        item => item.level === level
      )
      if (indexLevelAllowEdit !== -1) {
        const userType =
          indexLevelAllowEdit < ASSIGNEE_TYPE_CONFIG.length
            ? ASSIGNEE_TYPE_CONFIG[indexLevelAllowEdit]
            : `DYNAMIC_LEVEL_${indexLevelAllowEdit}`
        selectPeopleStore.setAssigneePopupType(userType)
        setIsVisibleSelectUserPopup(true)
      } else {
        message.warning('Không rõ chọn người loại nào!')
      }
    },
    [approvalLevels, selectPeopleStore]
  )

  const renderTitleStep = level => {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div>
          <span style={{ color: redPrimary }}>* </span>
          <span>{level.user?.department || level.name}</span>
        </div>
        {level.allow_edit && (
          <React.Fragment>
            <Tooltip title="Chọn người dùng" color={blue}>
              <Button
                icon={<UserOutlined style={{ color: blue }} />}
                onClick={() => handleOpenSelectUser(level.level)}
              />
            </Tooltip>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa cấp duyệt này?"
              okText="Xóa"
              cancelText="Không"
              onConfirm={() => onRemoveApprovalLevel?.(level.code)}>
              <Tooltip title="Xóa cấp duyệt" color="red">
                <Button icon={<DeleteOutlined style={{ color: 'red' }} />} />
              </Tooltip>
            </Popconfirm>
          </React.Fragment>
        )}
      </div>
    )
  }

  const renderDescStep = useCallback(
    level => {
      const levelAllowEditList = approvalLevels.filter(el => el.allow_edit)
      const assignTypeIndex = levelAllowEditList.findIndex(
        item => item.code === level.code
      )
      const assigneeType =
        assignTypeIndex < ASSIGNEE_TYPE_CONFIG.length
          ? ASSIGNEE_TYPE_CONFIG[assignTypeIndex]
          : `DYNAMIC_LEVEL_${assignTypeIndex}`

      const handleDeleteUser = level => {
        selectPeopleStore.handleRemoveSelect({
          assignee_type: ASSIGNEE_TYPE.USER,
          id: level.username,
          permission: assigneeType,
        })

        const updatedLevels = approvalLevels.map(item =>
          item.code === level.code
            ? { ...item, username: '', user: null }
            : item
        )

        setApprovalLevels(updatedLevels)

        proposalStore.setDefaultApprovalConfigUpdate(updatedLevels)
      }

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: level.allow_edit ? 6 : 14,
          }}>
          {level.user?.username || level.username ? (
            <Tag
              className={'tag-selected'}
              color={'blue'}
              closable={level.allow_edit}
              onClose={() => handleDeleteUser(level)}>
              <AvatarHasName
                imgId={level.user?.image_id}
                size={22}
                name={level.user?.full_name || level.username}
              />
            </Tag>
          ) : (
            <Tag style={{ marginTop: 6, borderRadius: 10 }}>
              Chưa gán người!
            </Tag>
          )}
          {renderStatus(level.status, statusStep === 'error')}
          {level.comment && level.comment !== '' && (
            <div style={{ marginTop: 4, fontStyle: 'italic' }}>
              <Typography.Paragraph
                ellipsis={{
                  rows: 15,
                  expandable: true,
                  onEllipsis: ellipsis => {
                    console.log('Ellipsis changed:', ellipsis)
                  },
                }}>
                <span style={{ fontWeight: 500, marginRight: 6 }}>Lý do:</span>
                {level.comment}
              </Typography.Paragraph>
            </div>
          )}
          {renderTime(level)}
        </div>
      )
    },
    [approvalLevels, selectPeopleStore, statusStep]
  )

  return styleCre != null ? (
    <StepsWrapper count={approvalLevels.length} i={4}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => (
          <Step
            key={level.level}
            title={renderTitleStep(level)}
            description={renderDescStep(level)}
          />
        ))}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  ) : (
    <StepsWrapper count={approvalLevels.length} i={5}>
      <Steps
        status={statusStep}
        className={'steps'}
        labelPlacement={'vertical'}
        current={currentStep}
        size={'small'}>
        {approvalLevels.map(level => (
          <Step
            key={level.level}
            title={renderTitleStep(level)}
            description={renderDescStep(level)}
          />
        ))}
      </Steps>
      <SelectPeoplePopup
        onlyOneUser
        handleCancelSelectUser={handleSubmitSelectUser}
        handleSubmitSelectUser={handleSubmitSelectUser}
        isVisibleSelectPeoplePopup={isVisibleSelectUserPopup}
        isUserDisableList={false}
      />
    </StepsWrapper>
  )
}

ProposalStepApprove.propTypes = {}

export default memo(
  inject(
    'authenticationStore',
    'proposalStore',
    'selectPeopleStore'
  )(observer(ProposalStepApprove))
)
